/**
 * Correcting a round that has already been recorded — the last one, or an earlier one.
 *
 * WHY EARLIER ROUNDS CAN BE EDITED (and what can't change)
 * ---------------------------------------------------------
 * Later rounds depend on an earlier round in exactly two ways:
 *   1. The dealer / round-wind rotation (`ui/rotation.ts`): the deal moves on only after a win
 *      by a NON-dealer; a draw or a win by the dealer keeps it. So all that matters about a
 *      round is whether it was a win or a draw, and whether the dealer won it.
 *   2. Running totals, which are simply the sum of every round's own payments.
 * Every round stores its own dealer, round wind and result, and its score comes only from its
 * own hand and context — never from another round. So changing an earlier round's tiles,
 * circumstances, self-draw/discard, discarder (or a draw's tenpai list) rewrites that one
 * round and shifts the totals from there on, and touches nothing else.
 *
 * What is therefore LOCKED on an earlier round: its winner (a different winner could change
 * whether the dealer won, which would move the deal for every later round) and whether it was
 * a win or a draw. The most recent round has nothing after it, so its winner may still change,
 * as it always could. Deleting a round remains last-round-only (that one really does shift
 * everything after it).
 *
 * Every edit keeps the round's ORIGINAL result (`RoundRecord.edited`) so it can be shown as
 * "Edited" and questioned later. Settings come from the game's own snapshot, never live app
 * state, so a correction can't be re-scored under different house rules (rule 5).
 */

import { getRuleset } from '../rules';
import { describeRejection } from '../rules/rejection';
import type { HandInput, Payment, PlayerId, WinContext } from '../rules/types';
import type { Wind } from '../rules/tiles';
import { summarise } from './summary';
import type { Game, RoundOriginal, RoundRecord } from './types';

export const WINNER_LOCKED =
  "The winner can't be changed on an earlier round — only on the most recent one. If the winner is wrong, delete back to that round and record it again.";
export const GAME_ENDED =
  'This game has ended. Tap "Ended this by mistake? Go back to this game" first, then correct the round.';

const WIND_ORDER: Wind[] = ['east', 'south', 'west', 'north'];

/** The winner's seat wind in a round, from that round's own dealer (never re-derived from the current table). */
function seatWindIn(game: Game, dealerId: PlayerId, winnerId: PlayerId): Wind {
  const dealerIndex = game.players.findIndex((p) => p.id === dealerId);
  const winnerIndex = game.players.findIndex((p) => p.id === winnerId);
  return WIND_ORDER[(winnerIndex - dealerIndex + 4) % 4]!;
}

export interface RoundEditResult {
  round: RoundRecord;
  /** Non-empty means the edit was refused and nothing changed. */
  problems: string[];
  /** True when the corrected round is identical to what was recorded (so nothing was marked "edited"). */
  unchanged: boolean;
}

/** Stand-in for a round that no longer exists, so a refusal can still carry a `round`. */
function missingRound(roundIndex: number): RoundRecord {
  return { index: roundIndex, outcome: 'win', dealer: '', prevailingWind: 'east', payments: [], recordedAt: 0 };
}

function refused(round: RoundRecord, ...problems: string[]): RoundEditResult {
  return { round, problems, unchanged: false };
}

/** Why this round can't be edited right now, or undefined if it can. */
export function editBlockedReason(game: Game, roundIndex: number): string | undefined {
  if (!game.rounds.some((r) => r.index === roundIndex)) return 'That round no longer exists.';
  if (game.endedAt) return GAME_ENDED;
  return undefined;
}

const positionOf = (game: Game, roundIndex: number): number => game.rounds.findIndex((r) => r.index === roundIndex);

function snapshot(r: RoundRecord): RoundOriginal {
  return {
    ...(r.winner !== undefined ? { winner: r.winner } : {}),
    ...(r.discarder !== undefined ? { discarder: r.discarder } : {}),
    ...(r.selfDraw !== undefined ? { selfDraw: r.selfDraw } : {}),
    ...(r.fan !== undefined ? { fan: r.fan } : {}),
    ...(r.effectiveFan !== undefined ? { effectiveFan: r.effectiveFan } : {}),
    ...(r.isLimit !== undefined ? { isLimit: r.isLimit } : {}),
    ...(r.tenpai !== undefined ? { tenpai: [...r.tenpai] } : {}),
    payments: r.payments.map((p) => ({ ...p })),
  };
}

/** Stamps a corrected round with the edit trail. The original is captured once, on the first edit, and never replaced. */
export function markEdited(previous: RoundRecord, updated: RoundRecord, now: number = Date.now()): RoundRecord {
  return {
    ...updated,
    edited: {
      at: now,
      count: (previous.edited?.count ?? 0) + 1,
      original: previous.edited?.original ?? snapshot(previous),
    },
  };
}

/** Everything about a round that an edit could change — used to spot an "edit" that changed nothing. */
function fingerprint(r: RoundRecord): string {
  return JSON.stringify([
    r.winner, r.discarder, r.selfDraw, r.winningTile, r.fan, r.effectiveFan, r.isLimit, r.basePoints,
    r.patterns, r.concealed, r.melds, r.flowers, r.claimedSet, r.tenpai, r.payments, r.photos,
  ]);
}

/**
 * Re-scores a recorded win from a corrected hand WITHOUT changing the game — used both to preview
 * the effect and (via `editWinHand`) to apply it. Runs the same validate -> score -> settle
 * pipeline as a fresh entry, so a bad correction is refused the way a bad entry would be.
 */
export function prepareWinHandEdit(
  game: Game,
  roundIndex: number,
  hand: HandInput,
  ctx: WinContext,
  photos?: string[],
  now: number = Date.now(),
): RoundEditResult {
  const blocked = editBlockedReason(game, roundIndex);
  const pos = positionOf(game, roundIndex);
  const round = game.rounds[pos];
  if (blocked || !round) return refused(round ?? missingRound(roundIndex), blocked ?? 'That round no longer exists.');
  if (round.outcome !== 'win') return refused(round, 'That round was a draw, not a win.');

  const isLast = pos === game.rounds.length - 1;
  if (!isLast && ctx.winner !== round.winner) return refused(round, WINNER_LOCKED);

  const ruleset = getRuleset(game.rulesetId);
  const settings = game.settings;
  // The round's own dealer and wind, as they were when it was played — not what the table looks like now.
  const fullCtx: WinContext = {
    ...ctx,
    dealer: round.dealer,
    prevailingWind: round.prevailingWind,
    seatWind: seatWindIn(game, round.dealer, ctx.winner),
  };

  const problems = ruleset.validate(hand, fullCtx, settings);
  if (problems.length > 0) return refused(round, ...problems);

  const result = ruleset.score(hand, fullCtx, settings);
  if (!result.meetsMinimum) {
    return refused(round, describeRejection(result));
  }

  const payments = ruleset.settle(result, fullCtx, game.players.map((p) => p.id), settings);

  const rebuilt: RoundRecord = {
    index: round.index,
    outcome: 'win',
    dealer: round.dealer,
    prevailingWind: round.prevailingWind,
    winner: fullCtx.winner,
    winningTile: hand.winningTile,
    selfDraw: fullCtx.selfDraw,
    ...(fullCtx.selfDraw ? {} : { discarder: fullCtx.discarder }),
    fan: result.fan,
    effectiveFan: result.effectiveFan,
    basePoints: result.basepoints,
    patterns: result.patterns,
    isLimit: result.isLimit,
    ...(photos?.length ? { photos } : {}),
    concealed: hand.concealed,
    melds: hand.melds,
    flowers: hand.flowers ?? [],
    ...(hand.claimedSet ? { claimedSet: true } : {}),
    payments,
    recordedAt: round.recordedAt,
    ...(round.edited ? { edited: round.edited } : {}),
  };

  if (fingerprint(rebuilt) === fingerprint(round)) return { round, problems: [], unchanged: true };
  return { round: markEdited(round, rebuilt, now), problems: [], unchanged: false };
}

/** Re-settles a recorded draw for a corrected tenpai list, without changing the game. */
export function prepareDrawTenpaiEdit(
  game: Game,
  roundIndex: number,
  tenpai: PlayerId[],
  now: number = Date.now(),
): RoundEditResult {
  const blocked = editBlockedReason(game, roundIndex);
  const pos = positionOf(game, roundIndex);
  const round = game.rounds[pos];
  if (blocked || !round) return refused(round ?? missingRound(roundIndex), blocked ?? 'That round no longer exists.');
  if (round.outcome !== 'draw') return refused(round, 'That round was a win, not a draw.');

  const ruleset = getRuleset(game.rulesetId);
  const payments = ruleset.settleDraw({ dealer: round.dealer, tenpai }, game.players.map((p) => p.id), game.settings);
  const rebuilt: RoundRecord = { ...round, tenpai, payments };

  if (fingerprint(rebuilt) === fingerprint(round)) return { round, problems: [], unchanged: true };
  return { round: markEdited(round, rebuilt, now), problems: [], unchanged: false };
}

/**
 * Writes a prepared correction into the game. Refuses anything that could move the deal for later
 * rounds — a different outcome, dealer, round wind, or (on an earlier round) winner — as a
 * backstop: the prepare functions never produce such a change, but this is what protects the
 * invariant if something else ever calls it.
 */
export function applyRoundEdit(game: Game, updated: RoundRecord): void {
  const pos = positionOf(game, updated.index);
  const old = game.rounds[pos];
  if (!old) throw new Error('That round no longer exists.');
  if (updated.outcome !== old.outcome || updated.dealer !== old.dealer || updated.prevailingWind !== old.prevailingWind) {
    throw new Error("A correction can't change whether the round was a win or draw, its dealer, or its round wind.");
  }
  if (pos !== game.rounds.length - 1 && updated.winner !== old.winner) throw new Error(WINNER_LOCKED);
  game.rounds[pos] = updated;
}

/** Corrects a recorded win (any round) and applies it. */
export function editWinHand(
  game: Game,
  roundIndex: number,
  hand: HandInput,
  ctx: WinContext,
  photos?: string[],
  now?: number,
): RoundEditResult {
  const result = prepareWinHandEdit(game, roundIndex, hand, ctx, photos, now);
  if (result.problems.length === 0 && !result.unchanged) applyRoundEdit(game, result.round);
  return result;
}

/** Corrects a recorded draw's tenpai list (any round) and applies it. */
export function editDrawTenpai(game: Game, roundIndex: number, tenpai: PlayerId[], now?: number): RoundEditResult {
  const result = prepareDrawTenpaiEdit(game, roundIndex, tenpai, now);
  if (result.problems.length === 0 && !result.unchanged) applyRoundEdit(game, result.round);
  return result;
}

// ---------------------------------------------------------------------------
// The "before / after" shown before a correction is saved
// ---------------------------------------------------------------------------

export interface EditImpactPlayer {
  player: PlayerId;
  /** This round's payment to the player, as recorded and as it would become. */
  roundBefore: number;
  roundAfter: number;
  /** Their running total across the whole game, before and after. */
  totalBefore: number;
  totalAfter: number;
  rankBefore: number;
  rankAfter: number;
}

export interface EditImpact {
  roundIndex: number;
  /** Fan actually paid, before and after (win rounds). */
  fanBefore?: number;
  fanAfter?: number;
  players: EditImpactPlayer[];
  rankingChanged: boolean;
  /** Rounds after this one — their own results are untouched; only the totals move. */
  laterRounds: number;
}

const paymentOf = (payments: Payment[], player: PlayerId): number =>
  payments.find((p) => p.player === player)?.delta ?? 0;

/** What saving a prepared correction would do to everyone's score and ranking. Does not change the game. */
export function describeEditImpact(game: Game, updated: RoundRecord): EditImpact {
  const pos = positionOf(game, updated.index);
  const old = game.rounds[pos];
  if (!old) throw new Error('That round no longer exists.');

  const before = summarise(game).players;
  const after = summarise({ ...game, rounds: game.rounds.map((r, i) => (i === pos ? updated : r)) }).players;
  const find = (list: typeof before, id: PlayerId) => list.find((p) => p.player === id)!;

  const players = game.players.map((p): EditImpactPlayer => ({
    player: p.id,
    roundBefore: paymentOf(old.payments, p.id),
    roundAfter: paymentOf(updated.payments, p.id),
    totalBefore: find(before, p.id).totalScore,
    totalAfter: find(after, p.id).totalScore,
    rankBefore: find(before, p.id).rank,
    rankAfter: find(after, p.id).rank,
  }));

  return {
    roundIndex: updated.index,
    ...(old.effectiveFan !== undefined ? { fanBefore: old.effectiveFan } : {}),
    ...(updated.effectiveFan !== undefined ? { fanAfter: updated.effectiveFan } : {}),
    players,
    rankingChanged: players.some((p) => p.rankBefore !== p.rankAfter),
    laterRounds: game.rounds.length - 1 - pos,
  };
}
